import { createContext, useState, useMemo, useCallback, useEffect } from 'react';
import { createTheme, ThemeOptions, PaletteColorOptions } from '@mui/material/styles';
import { nbNO } from '@mui/material/locale';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';
import { getValueFromLs, setKeyValuePairToLS } from '@/shared/services/localstorage';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: PaletteColorOptions;
  }
  interface PaletteOptions {
    neutral?: PaletteColorOptions;
  }
}

const typography: ThemeOptions['typography'] = {
  fontFamily: ['Source Sans Pro', 'sans-serif'].join(','),
  fontSize: 12,
  h1: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 40 },
  h2: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 32 },
  h3: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 24 },
  h4: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 20 },
  h5: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 16 },
  h6: { fontFamily: 'Source Sans Pro, sans-serif', fontSize: 14 },
};

export const themeSettings = (mode: 'light' | 'dark'): ThemeOptions => {
  return {
    components: {
      MuiCardContent: {
        styleOverrides: {
          root: {
            '&:last-child': {
              paddingBottom: 16,
            },
          },
        },
      },
    },
    palette: {
      mode: mode,
      ...(mode === 'light'
        ? {
            primary: {
              main: '#0f172a', // Deep Slate (Instead of #000). Looks black, but rich.
              light: '#334155',
              dark: '#020617',
            },
            secondary: {
              main: '#ffa737', // Your Brand Orange
              light: '#ffc947',
              dark: '#c67c00',
            },
            neutral: {
              dark: '#334155',
              main: '#64748b', // Blue-ish Grey (Harmonizes with orange)
              light: '#cbd5e1',
            },
            background: {
              default: '#f8fafc', // Cool White (Not yellow-ish #EEEEEE)
              paper: '#ffffff',
            },
            text: {
              primary: '#0f172a', // Matches Primary Main
              secondary: '#475569', // Softer Slate Text
            },
            divider: '#e2e8f0', // Clean cool grey divider
          }
        : {
            primary: {
              main: '#f1f5f9', // Soft White (Slate 100) - easier on eyes than #fff
              light: '#ffffff',
              dark: '#cbd5e1',
            },
            secondary: {
              main: '#ffa737', // Pops vividly against the dark blue background
              light: '#ffb74d',
              dark: '#f57c00',
            },
            neutral: {
              dark: '#0f172a',
              main: '#94a3b8', // Readable cool grey
              light: '#1e293b',
            },
            background: {
              default: '#0b1120', // Deep Midnight Blue (Much richer than #121212)
              paper: '#1e293b', // Lighter Slate for cards
            },
            text: {
              primary: '#f1f5f9',
              secondary: '#94a3b8',
            },
            divider: '#334155', // Low contrast border for dark mode
          }),
    },
    typography: typography,
  };
};

interface IColorModeContext {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
}

export const ColorModeContext = createContext<IColorModeContext>({
  toggleColorMode: () => {},
  mode: 'light',
});

type ThemeType = 'light' | 'dark';

export const useMode = () => {
  const [mode, setMode] = useState<ThemeType>(() => {
    const stored = getValueFromLs(EDITOR_LS_CONSTANTS.THEME);
    return stored === 'light' || stored === 'dark' ? (stored as ThemeType) : 'light';
  });

  useEffect(() => {
    setKeyValuePairToLS(EDITOR_LS_CONSTANTS.THEME, mode);
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: toggleColorMode,
      mode,
    }),
    [mode, toggleColorMode]
  );

  const theme = useMemo(() => createTheme(themeSettings(mode), nbNO), [mode]);

  return [theme, colorMode] as const;
};
